import { useGetAdminUsers } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useCallback, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { ChevronDown, ChevronUp, Eye, EyeOff, Shield, Newspaper, Ban, Star, Wrench, MapPin, Monitor, UserPlus, Smartphone, Globe } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const AYRSHIRE_TOWNS = ["Ayr", "Kilmarnock", "Irvine", "Prestwick", "Troon", "Saltcoats", "Ardrossan", "Largs", "Kilwinning", "Stewarton", "Maybole", "Girvan", "Cumnock", "Auchinleck", "Newmilns", "Kilbirnie", "Beith", "Dalry"];

interface AdminUser {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  role: string;
  town?: string;
  createdAt: string;
  isModerator?: boolean;
  communityBanned?: boolean;
  isJournalist?: boolean;
  isSponsor?: boolean;
  signupSource?: string | null;
}

interface LoginLogEntry {
  id: number;
  ip?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  userAgent?: string | null;
  source?: string | null;
  createdAt: string;
}

function LoginHistoryPanel({ userId }: { userId: number }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState<LoginLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem("ayrshire_token");
    fetch(`/api/admin/users/${userId}/login-logs`, {
      headers: { Authorization: `Bearer ${token ?? ""}` },
    })
      .then(r => r.json())
      .then((data: LoginLogEntry[]) => setLogs(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, [open, userId]);

  function formatAgent(ua?: string | null): string {
    if (!ua) return "Unknown";
    if (/mobile/i.test(ua)) return "Mobile";
    if (/tablet/i.test(ua)) return "Tablet";
    return "Desktop";
  }

  function formatLocation(log: LoginLogEntry): string {
    const parts = [log.city, log.region, log.country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Location unknown";
  }

  return (
    <div className="border-t border-slate-200 pt-3 mt-1">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 mb-2"
      >
        <MapPin size={12} />
        {open ? "Hide" : "View"} Login History
      </button>
      {open && (
        <div className="space-y-1.5 max-h-52 overflow-y-auto">
          {loading && <p className="text-xs text-slate-400">Loading…</p>}
          {!loading && logs.length === 0 && (
            <p className="text-xs text-slate-400">No login records yet.</p>
          )}
          {logs.map(log => (
            <div key={log.id} className="flex items-start gap-2 bg-white border border-slate-100 rounded-lg px-2.5 py-2 text-xs">
              {log.source === "mobile"
                ? <Smartphone size={11} className="mt-0.5 text-violet-400 shrink-0" />
                : <Globe size={11} className="mt-0.5 text-sky-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold ${log.source === "mobile" ? "text-violet-600" : "text-sky-600"}`}>
                    {log.source === "mobile" ? "App" : log.source === "web" ? "Web" : formatAgent(log.userAgent)}
                  </span>
                  <span className="font-mono text-slate-700">{log.ip ?? "—"}</span>
                  <span className="text-slate-500">{formatLocation(log)}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {new Date(log.createdAt).toLocaleString("en-GB")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TradeProfilePanel({ userId, onProvisioned }: { userId: number; onProvisioned: () => void }) {
  const { toast } = useToast();
  const categories = useCategories("job");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    businessName: "", categories: [] as string[], serviceAreas: [] as string[],
    phone: "", email: "", description: "", yearsExperience: "", insuranceStatus: false, emergencyCallout: false,
  });

  useEffect(() => {
    const token = localStorage.getItem("ayrshire_token");
    const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    fetch(`/api/admin/users/${userId}/trade-profile`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setForm({
          businessName: data.businessName ?? "",
          categories: data.categories ?? [],
          serviceAreas: data.serviceAreas ?? [],
          phone: data.phone ?? "",
          email: data.email ?? "",
          description: data.description ?? "",
          yearsExperience: data.yearsExperience != null ? String(data.yearsExperience) : "",
          insuranceStatus: data.insuranceStatus ?? false,
          emergencyCallout: data.emergencyCallout ?? false,
        });
      })
      .finally(() => setLoading(false));
  }, [userId]);

  function toggleItem(arr: string[], item: string) {
    return arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
  }

  async function handleSave() {
    if (!form.businessName.trim() || form.categories.length === 0) {
      toast({ title: "Business name and at least one category are required", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const r = await fetch(`/api/admin/users/${userId}/trade-profile`, {
        method: "POST", headers,
        body: JSON.stringify({ ...form, yearsExperience: form.yearsExperience ? Number(form.yearsExperience) : null }),
      });
      if (!r.ok) throw new Error();
      toast({ title: "Trade profile saved — user is now a tradesperson" });
      onProvisioned();
    } catch {
      toast({ title: "Failed to save trade profile", variant: "destructive" });
    } finally { setSaving(false); }
  }

  if (loading) return <p className="text-xs text-slate-400 py-2">Loading trade profile…</p>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Business / Trading Name <span className="text-red-400">*</span></label>
          <Input value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))} className="h-8 text-sm" placeholder="e.g. Ayrshire Plumbing Services" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Phone</label>
          <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-8 text-sm" placeholder="07700 900000" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
          <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-8 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Years Experience</label>
          <Input type="number" value={form.yearsExperience} onChange={e => setForm(f => ({ ...f, yearsExperience: e.target.value }))} className="h-8 text-sm" placeholder="e.g. 10" />
        </div>
        <div className="flex items-center gap-4 pt-5">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.insuranceStatus} onChange={e => setForm(f => ({ ...f, insuranceStatus: e.target.checked }))} className="rounded" />
            Insured
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.emergencyCallout} onChange={e => setForm(f => ({ ...f, emergencyCallout: e.target.checked }))} className="rounded" />
            Emergency callout
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">Trade Categories <span className="text-red-400">*</span></label>
        <div className="flex flex-wrap gap-1.5">
          {categories.map(cat => (
            <button key={cat} type="button"
              onClick={() => setForm(f => ({ ...f, categories: toggleItem(f.categories, cat) }))}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.categories.includes(cat) ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/50"}`}
            >{cat}</button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">Service Areas</label>
        <div className="flex flex-wrap gap-1.5">
          {AYRSHIRE_TOWNS.map(town => (
            <button key={town} type="button"
              onClick={() => setForm(f => ({ ...f, serviceAreas: toggleItem(f.serviceAreas, town) }))}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.serviceAreas.includes(town) ? "bg-secondary text-white border-secondary" : "bg-white text-slate-600 border-slate-200 hover:border-secondary/50"}`}
            >{town}</button>
          ))}
        </div>
      </div>

      <Button size="sm" disabled={saving} onClick={() => void handleSave()} className="bg-primary hover:bg-primary/90 h-7 text-xs px-3">
        {saving ? "Saving…" : "Save Trade Profile"}
      </Button>
    </div>
  );
}

const ROLE_COLOURS: Record<string, string> = {
  admin: "bg-purple-100 text-purple-700",
  trade: "bg-blue-100 text-blue-700",
  tradesperson: "bg-blue-100 text-blue-700",
  business: "bg-sky-100 text-sky-700",
  resident: "bg-emerald-100 text-emerald-700",
  user: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700",
};

const ROLES = ["resident", "tradesperson", "business", "admin", "suspended"];

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function FlagToggle({
  active,
  label,
  icon: Icon,
  activeColor,
  onToggle,
  disabled,
}: {
  active: boolean;
  label: string;
  icon: React.ElementType;
  activeColor: string;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      title={active ? `Remove ${label}` : `Grant ${label}`}
      className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
        active
          ? `${activeColor} border-current/30`
          : "text-slate-400 border-slate-200 hover:border-slate-300 bg-white"
      }`}
    >
      <Icon size={11} />
      {label}
    </button>
  );
}

function UserCard({ u, myId, onRefetch }: { u: AdminUser; myId: number; onRefetch: () => void }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flagSaving, setFlagSaving] = useState<string | null>(null);
  const [showPw, setShowPw] = useState(false);
  const [localFlags, setLocalFlags] = useState({
    isModerator: u.isModerator ?? false,
    communityBanned: u.communityBanned ?? false,
    isJournalist: u.isJournalist ?? false,
    isSponsor: u.isSponsor ?? false,
  });
  const [form, setForm] = useState({ name: u.name, email: u.email, phone: u.phone ?? "", town: u.town ?? "", role: u.role, newPassword: "" });

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const body: Record<string, string> = {};
      if (form.name !== u.name) body.name = form.name;
      if (form.email !== u.email) body.email = form.email;
      if (form.phone !== (u.phone ?? "")) body.phone = form.phone;
      if (form.town !== (u.town ?? "")) body.town = form.town;
      if (form.role !== u.role) body.role = form.role;
      if (form.newPassword.trim()) body.newPassword = form.newPassword;
      if (Object.keys(body).length === 0) { toast({ title: "No changes to save" }); setSaving(false); return; }
      const res = await fetch(`/api/admin/users/${u.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "User updated" });
      setForm(f => ({ ...f, newPassword: "" }));
      setExpanded(false);
      onRefetch();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }, [form, u, toast, onRefetch]);

  const handleSuspend = useCallback(async () => {
    const isSuspended = u.role === "suspended";
    setSaving(true);
    try {
      await fetch(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ role: isSuspended ? "resident" : "suspended" }),
      });
      toast({ title: isSuspended ? "User unsuspended" : "User suspended" });
      onRefetch();
    } catch {
      toast({ title: "Action failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }, [u, toast, onRefetch]);

  const toggleFlag = useCallback(async (flag: keyof typeof localFlags) => {
    setFlagSaving(flag);
    const newVal = !localFlags[flag];
    setLocalFlags(f => ({ ...f, [flag]: newVal }));
    try {
      const res = await fetch(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ [flag]: newVal }),
      });
      if (!res.ok) throw new Error();
      const label = flag === "isModerator" ? "Moderator" : flag === "isJournalist" ? "Journalist" : flag === "isSponsor" ? "Sponsor" : "Community Ban";
      toast({ title: newVal ? `${label} granted` : `${label} removed` });
      onRefetch();
    } catch {
      setLocalFlags(f => ({ ...f, [flag]: !newVal }));
      toast({ title: "Failed to update", variant: "destructive" });
    } finally {
      setFlagSaving(null);
    }
  }, [localFlags, u.id, toast, onRefetch]);

  const cancelEdit = useCallback(() => {
    setExpanded(false);
    setForm({ name: u.name, email: u.email, phone: u.phone ?? "", town: u.town ?? "", role: u.role, newPassword: "" });
  }, [u]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-primary">{u.name.charAt(0).toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm">{u.name}</p>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOURS[u.role] ?? "bg-slate-100 text-slate-500"}`}>
              {u.role}
            </span>
            {localFlags.isModerator && (
              <span className="flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                <Shield size={10} /> Mod
              </span>
            )}
            {localFlags.isJournalist && (
              <span className="flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                <Newspaper size={10} /> Press
              </span>
            )}
            {localFlags.communityBanned && (
              <span className="flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                <Ban size={10} /> Community Ban
              </span>
            )}
            {localFlags.isSponsor && (
              <span className="flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                <Star size={10} /> Sponsor
              </span>
            )}
            {u.signupSource === "mobile" ? (
              <span className="flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                <Smartphone size={10} /> App
              </span>
            ) : (
              <span className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${u.signupSource === "web" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-400"}`}>
                <Globe size={10} /> Web
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}{u.town ? ` · ${u.town}` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          <span className="text-xs text-slate-400 hidden md:block">
            {new Date(u.createdAt).toLocaleDateString("en-GB")}
          </span>
          {u.id !== myId && (
            <Button
              size="sm"
              variant={u.role === "suspended" ? "outline" : "destructive"}
              disabled={saving}
              onClick={handleSuspend}
              className="text-xs h-7 px-2"
            >
              {u.role === "suspended" ? "Unsuspend" : "Suspend"}
            </Button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-primary font-semibold px-2 py-1 rounded-lg hover:bg-primary/5 transition-colors border border-primary/20"
          >
            Edit {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>

      {/* Flag toggles */}
      {u.id !== myId && (
        <div className="px-4 pb-3 flex gap-2 flex-wrap border-t border-slate-50 pt-2">
          <FlagToggle
            active={localFlags.isModerator}
            label="Moderator"
            icon={Shield}
            activeColor="text-indigo-600 bg-indigo-50"
            onToggle={() => void toggleFlag("isModerator")}
            disabled={flagSaving !== null}
          />
          <FlagToggle
            active={localFlags.isJournalist}
            label="Journalist"
            icon={Newspaper}
            activeColor="text-amber-600 bg-amber-50 dark:bg-amber-900/20"
            onToggle={() => void toggleFlag("isJournalist")}
            disabled={flagSaving !== null}
          />
          <FlagToggle
            active={localFlags.communityBanned}
            label="Community Ban"
            icon={Ban}
            activeColor="text-red-600 bg-red-50"
            onToggle={() => void toggleFlag("communityBanned")}
            disabled={flagSaving !== null}
          />
          <FlagToggle
            active={localFlags.isSponsor}
            label="Sponsor"
            icon={Star}
            activeColor="text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20"
            onToggle={() => void toggleFlag("isSponsor")}
            disabled={flagSaving !== null}
          />
        </div>
      )}

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Full Name</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Phone</label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-8 text-sm" placeholder="e.g. 07700 900000" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={form.town} onChange={e => setForm(f => ({ ...f, town: e.target.value }))} className="h-8 text-sm" placeholder="e.g. Ayr" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Role</label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full h-8 border border-input bg-white rounded-md px-2 text-sm"
              >
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Reset Password <span className="font-normal text-slate-400">(leave blank to keep existing)</span>
            </label>
            <div className="relative max-w-xs">
              <Input
                type={showPw ? "text" : "password"}
                value={form.newPassword}
                onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
                placeholder="Enter new password…"
                className="h-8 text-sm pr-8"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={saving} onClick={handleSave} className="bg-primary hover:bg-primary/90 h-7 text-xs px-3">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
            <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 text-xs px-3">
              Cancel
            </Button>
          </div>

          {/* Trade Profile Section */}
          <div className="border-t border-slate-200 pt-3 mt-1">
            <button
              onClick={() => setTradeOpen(o => !o)}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 dark:text-blue-300 mb-2"
            >
              <Wrench size={12} />
              {tradeOpen ? "Hide" : "Set up"} Trade Profile
              {u.role === "tradesperson" && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold">Active</span>}
            </button>
            {tradeOpen && (
              <TradeProfilePanel userId={u.id} onProvisioned={onRefetch} />
            )}
          </div>

          {/* Login History Section */}
          <LoginHistoryPanel userId={u.id} />
        </div>
      )}
    </div>
  );
}

const ACCOUNT_ROLES = ["resident", "business", "tradesperson", "admin"];

function CreateAccountDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "resident", town: "" });

  function reset() {
    setForm({ name: "", email: "", password: "", role: "resident", town: "" });
    setShowPw(false);
  }

  async function handleSubmit() {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast({ title: "Name, email and password are required", variant: "destructive" }); return;
    }
    if (form.password.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(form),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data.error ?? "Failed");
      toast({ title: `Account created for ${form.email}` });
      reset();
      onCreated();
      onClose();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed to create account", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Full Name <span className="text-red-400">*</span></label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jane Smith" className="h-8 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Email <span className="text-red-400">*</span></label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@example.com" className="h-8 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Password <span className="text-red-400">*</span></label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min 8 characters"
                className="h-8 text-sm pr-8"
              />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="w-full h-8 border border-input bg-white rounded-md px-2 text-sm">
                {ACCOUNT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={form.town} onChange={e => setForm(f => ({ ...f, town: e.target.value }))} placeholder="e.g. Ayr" className="h-8 text-sm" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" disabled={saving} onClick={() => void handleSubmit()} className="bg-primary hover:bg-primary/90 h-8 text-xs px-4">
              {saving ? "Creating…" : "Create Account"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { reset(); onClose(); }} className="h-8 text-xs px-3">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { user: me, isLoading: authLoading } = useAuth();
  const { data: users, isLoading, refetch } = useGetAdminUsers();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  if (authLoading) return null;
  if (me?.role !== "admin") return <Redirect to="/" />;

  const filtered = (users?.items ?? []).filter((u) => {
    const q = search.toLowerCase();
    return (
      !q ||
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u as unknown as AdminUser).town?.toLowerCase().includes(q)
    );
  }) as AdminUser[];

  return (
    <AdminLayout current="/admin/users">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-primary">Users</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{users?.total ?? 0} total</span>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90 h-8 text-xs gap-1.5">
              <UserPlus size={13} /> Create Account
            </Button>
          </div>
        </div>

        <CreateAccountDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => void refetch()} />

        <Input
          placeholder="Search by name, email, or town..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u) => (
              <UserCard
                key={u.id}
                u={u}
                myId={me?.id ?? -1}
                onRefetch={() => void refetch()}
              />
            ))}
            {filtered.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-xl px-4 py-8 text-center text-slate-400 text-sm">
                No users match your search.
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
